import React, { useRef, useState } from "react";
import { Upload, X, User } from "lucide-react";

interface WizardImageUploadProps {
  onImageSelect: (base64: string) => void;
  onSkip: () => void;
  currentImage?: string;
}

export const WizardImageUpload: React.FC<WizardImageUploadProps> = ({
  onImageSelect,
  onSkip,
  currentImage,
}) => {
  const [preview, setPreview] = useState<string | null>(currentImage || null);
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar tipo de arquivo
    if (!file.type.startsWith("image/")) {
      alert("Por favor, selecione apenas arquivos de imagem.");
      return;
    }

    // Validar tamanho (máximo 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert("A imagem deve ter no máximo 5MB.");
      return;
    }

    setIsProcessing(true);

    try {
      // Criar imagem para redimensionar se necessário
      const img = new Image();
      const reader = new FileReader();

      reader.onload = (event) => {
        img.src = event.target?.result as string;
      };

      img.onload = () => {
        // Redimensionar se a imagem for muito grande
        const maxWidth = 800;
        const maxHeight = 800;
        let width = img.width;
        let height = img.height;

        if (width > maxWidth || height > maxHeight) {
          if (width > height) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          } else {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }
        }

        // Criar canvas para redimensionar
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");

        if (ctx) {
          ctx.drawImage(img, 0, 0, width, height);

          // Converter para base64 com qualidade reduzida
          const base64String = canvas.toDataURL("image/jpeg", 0.8);
          console.log("📸 Imagem processada:", {
            originalSize: file.size,
            base64Length: base64String.length,
            dimensions: `${width}x${height}`,
          });

          setPreview(base64String);
        }

        setIsProcessing(false);
      };

      img.onerror = () => {
        console.error("Erro ao carregar imagem");
        alert("Erro ao processar a imagem. Tente novamente.");
        setIsProcessing(false);
      };

      reader.readAsDataURL(file);
    } catch (error) {
      console.error("Erro ao processar imagem:", error);
      alert("Erro ao processar a imagem. Tente novamente.");
      setIsProcessing(false);
    }
  };

  const handleRemoveImage = () => {
    setPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleConfirm = () => {
    if (preview) {
      onImageSelect(preview);
    }
  };

  return (
    <div className="flex flex-col gap-4 p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
      {/* Preview ou placeholder */}
      <div className="flex flex-col items-center gap-3">
        <div className="relative w-32 h-32 rounded-full overflow-hidden bg-gray-100 dark:bg-gray-700 border-4 border-purple-100 dark:border-purple-900">
          {preview ? (
            <>
              <img
                src={preview}
                alt="Preview"
                className="w-full h-full object-cover"
              />
              <button
                type="button"
                onClick={handleRemoveImage}
                className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors shadow-lg"
                aria-label="Remover imagem"
              >
                <X size={16} />
              </button>
            </>
          ) : (
            <div className="flex items-center justify-center w-full h-full text-gray-400">
              <User size={48} />
            </div>
          )}
        </div>

        {/* Botão de upload */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          className="hidden"
          id="wizard-image-upload"
        />
        <label
          htmlFor="wizard-image-upload"
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium cursor-pointer transition-colors ${
            isProcessing
              ? "bg-gray-300 text-gray-500 cursor-not-allowed"
              : "bg-purple-100 text-purple-700 hover:bg-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:hover:bg-purple-900/50"
          }`}
        >
          <Upload size={18} />
          {preview ? "Trocar Foto" : "Escolher Foto"}
        </label>

        <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
          Formatos aceitos: JPG, PNG, WEBP
          <br />
          Tamanho máximo: 5MB
        </p>
      </div>

      {/* Botões de ação */}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onSkip}
          disabled={isProcessing}
          className="flex-1 px-4 py-2.5 rounded-lg font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Pular
        </button>
        {preview && (
          <button
            type="button"
            onClick={handleConfirm}
            disabled={isProcessing}
            className="flex-1 px-4 py-2.5 rounded-lg font-medium text-white bg-purple-600 hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Confirmar
          </button>
        )}
      </div>
    </div>
  );
};
